<template>
  <HeaderNavbar
    :title="order?.title || 'Your order'"
    :breadcrumbs="listBreadcrumbs"
    title-size="sm"
    :no-border="true"
    :no-bottom-margin="true">
    <template #subtitle>
      <div class="tw:mt-1 tw:flex tw:items-center tw:gap-3">
        <span class="tw:text-14 tw:text-gray-500">{{ order?.provider_job_id }}</span>
        <LeadStatusPill v-if="order?.lead_order_status" :status="order.lead_order_status" />
      </div>
    </template>
    <template #right-side v-if="featureStore.featureCanCreateEditOrder">
      <div class="d-flex align-items-center gap-2">
        <LeadButton
          v-if="allowSelfService"
          variant="white"
          @click="providerStore.providers.length > 0 ? (syncModal = true) : ''"
          :disabled="isDisabled">
          <template #prepend-icon>
            <IconRefresh :size="20" />
          </template>
          {{ $t('btn.synchronize') }}
        </LeadButton>
        <LeadButton
          v-if="order?.lead_order_status === 'running' && featureStore.featureCanFinishCampaigns"
          variant="outline"
          v-tooltip="`${$t('orders.details.finish_campaigns')}`"
          @click="finishConfirmationDialog = true">
          <IconChecks :size="20" />
        </LeadButton>
        <LeadButton
          v-else-if="showDeleteButton"
          variant="outline-danger"
          @click="deleteConfirmationDialog = true"
          v-tooltip="`${$t('orders.details.delete_campaigns')}`">
          <IconTrash :size="20" />
        </LeadButton>
      </div>
    </template>
  </HeaderNavbar>
  <div class="container-lead white-background">
    <Tabs
      :modelValue="route.name as string"
      @update:modelValue="(pathName: string) => router.push({ name: pathName, params: { id: orderId } })"
      :tabs="orderTabs"
      class="nav-bordered mb-4 sticky-top bg-white tw:-mx-8! tw:px-8!" />
    <div v-if="order">
      <router-view :orderId="orderId" :allowPreviews="allowPreviews" :order="order" />
    </div>
  </div>
  <LiConfirmationDialog
    v-model:dialogShow="deleteConfirmationDialog"
    :confirmButtonLabel="$t('btn.delete')"
    @onContinue="deleteOrder">
    <h1 class="mb-3">{{ $t('orders.details.delete_order.title') }}</h1>
    <p>{{ $t('orders.details.delete_order.description') }}</p>
  </LiConfirmationDialog>
  <FinalizeCampaignsDialog v-model="finishConfirmationDialog" :orderId="orderId" @on-finished="fetchOrder(false)">
  </FinalizeCampaignsDialog>

  <SyncModal
    v-if="allowSelfService"
    v-model="syncModal"
    :orderId="orderId"
    :jobReferenceId="order?.provider_job_id"
    :confirmedByCustomer="order?.confirmed_by_customer"
    :startDate="order?.startdate" />
</template>
<script setup lang="ts">
import { request } from '@/api/client';

import translator from '@/locale/translator';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import type { Order } from '@/types/types';
import { getOrderRoute } from '@/func/orderCreation';
import { ConfirmationDialog as LiConfirmationDialog, Tabs } from '@prospective/lithium';
import FinalizeCampaignsDialog from '@/components/orders/FinalizeCampaignsDialog.vue';
import { IconTrash, IconChecks, IconRefresh } from '@tabler/icons-vue';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import HeaderNavbar from '@/components/header/HeaderNavbar.vue';
import LeadStatusPill from '@/components/lead-design/LeadStatusPill.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import SyncModal from '@/components/orders/SyncModal.vue';
import { useProviderStore } from '@/stores/providers';
import { storeToRefs } from 'pinia';
import { useAutopilotRouteReady } from '@/composables/useAutopilotRouteReady';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import { toAutopilotOrder } from '@/autopilot/functions/orders/orderContext';

const route = useRoute();
const router = useRouter();
const toast = useToast();

const orderId = route.params.id as string;
const featureStore = useCustomerFeaturesStore();
const providerStore = useProviderStore();

const order = ref<Order | undefined>();
const isLoading = ref(true);
const deleteConfirmationDialog = ref(false);
const finishConfirmationDialog = ref(false);
const syncModal = ref(false);
const { providersWithUpdatedStatus } = storeToRefs(providerStore);
const routeReady = useAutopilotRouteReady();
useAutopilotContext('current_order', () => toAutopilotOrder(order.value));

/* -------------------------------- COMPUTED -------------------------------- */
const showDeleteButton = computed(() => {
  return ['draft', 'to_be_confirmed', 'confirmed'].includes(order.value?.lead_order_status || '');
});
const listBreadcrumbs = computed(() => [
  { label: translator.get('nav.orders'), path: { name: 'orders.list' } },
  {
    label: order.value?.title || translator.get('orders.details.title'),
    path: { name: 'order.detail.info', params: { id: orderId } },
  },
]);
const allowPreviews = computed(() => Boolean(featureStore.featureOrderManagment?.allow_previews));
const allowSelfService = computed(() => Boolean(featureStore.featureOrderManagment?.self_service));
// only show the kanban tab when the order has at least one publication and the customer collects candidate data
const hasPublication = computed(() => Boolean(order.value?.publications?.data?.length));
const showKanban = computed(
  () => Boolean(featureStore.featureEcDataVault?.collect_candidate_data) && hasPublication.value
);
const orderTabs = computed(() => {
  const tabs = [
    {
      label: translator.get('orders.details.info'),
      value: 'order.detail.info',
      isLoading: isLoading.value,
      badge: undefined,
    },
    {
      label: translator.get('orders.details.packages'),
      value: 'order.detail.packages',
      isLoading: false,
      badge: undefined,
    },
    {
      label: translator.get('orders.details.banners'),
      value: 'order.detail.banners',
      isLoading: false,
      badge: undefined,
    },
    {
      label: translator.get('orders.details.support'),
      value: 'order.detail.support',
      isLoading: false,
      // badge: '2' || true, //if you want to show notification badge
    },
    {
      label: translator.get('orders.details.job_posting'),
      value: 'order.detail.job_posting',
      isLoading: false,
      // badge: '2' || true, //if you want to show notification badge
    },
  ];

  if (allowPreviews.value)
    tabs.splice(1, 0, {
      label: translator.get('orders.details.preview'),
      value: 'order.detail.preview',
      isLoading: false,
      badge: undefined,
    });

  if (allowSelfService.value)
    tabs.splice(2, 0, {
      label: translator.get('orders.details.settings'),
      value: 'order.detail.settings',
      isLoading: isLoading.value,
      badge: undefined,
    });

  // temporarily hidden
  // if (showStats.value)
  //   tabs.splice(1, 0, {
  //     label: translator.get('orders.details.stats'),
  //     value: 'order.detail.stats',
  //     isLoading: false,
  //     badge: undefined,
  //   });

  if (showKanban.value)
    tabs.push({
      label: translator.get('orders.details.kanban'),
      value: 'order.detail.kanban',
      isLoading: false,
      badge: undefined,
    });

  return tabs;
});

const isDisabled = computed(() => {
  // Disable sync button if there are no providers or ALL providers or the order are in finished state
  return (
    providerStore.providers.length === 0 ||
    providerStore.providers.every((provider) => provider.finished) ||
    order.value?.lead_order_status === 'finished'
  );
});

onMounted(async () => {
  fetchOrder();

  // if self-service is allowed, and there are providers, start polling for campaign status updates
  if (allowSelfService.value) {
    providerStore.clearProviders();
    const providers = await providerStore.getProviders(orderId);

    if (providers && providers.length > 0) {
      providerStore.startPollingCampaigns(orderId);
    }
  }
});

onUnmounted(() => {
  if (allowSelfService.value) {
    providerStore.stopPollingCampaigns();
  }
  providerStore.clearProviders();
});

const fetchOrder = async (showLoader = true) => {
  try {
    // If use has no feature permission, redirect to home
    if (!featureStore.featureOrderManagment) return router.replace({ name: 'home' });

    isLoading.value = showLoader;
    const fetchOrder = await request('GET', `/v1/account/order/${orderId}`, {
      resolve: 'company,company.logo_image,stats_cache,creator,publications',
    });
    order.value = fetchOrder?.data;

    // this page only handles finalized orders; a draft (e.g. linked to directly, or one that
    // reverted to draft) belongs back in the creation wizard, not rendered here
    if (order.value?.is_draft) {
      router.replace(getOrderRoute(order.value, allowSelfService.value));
      return;
    }

    isLoading.value = false;
    routeReady();
  } catch (error) {
    console.error('Error fetching order:', error);
    toast.error('Something went wrong. Please try again later');
    isLoading.value = false;
    routeReady();
  }
};

const deleteOrder = async () => {
  const response = await request('DELETE', `/v1/account/order/${orderId}`, {}, {});

  if (response?.success) {
    router.push({
      name: 'orders.list',
    });
  }
  toast.success('The order has been deleted successfully', { icon: 'ti ti-check' });
};

// if there is a change in providersWithUpdatedStatus, refetch the order to get updated status
watch(providersWithUpdatedStatus, (newVal) => {
  if (newVal && newVal.length > 0) {
    fetchOrder(false);
  }
});
</script>
<style scoped>
* {
  font-family: var(--font-brand);
}

.container-lead.white-background {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
}
</style>
